// 产品矩阵系统 - 画布编辑页面
import React, { useEffect, useState, useCallback } from 'react';
import { 
  Grid3X3, 
  Grid2X2, 
  LayoutGrid, 
  Download, 
  Trash2, 
  Save,
  Moon,
  Sun,
  ChevronDown,
  Tag,
  Undo2,
  Redo2,
  FolderOpen,
  FilePlus,
  History,
  Check,
  Edit3,
  Loader2,
  SaveAll,
} from 'lucide-react';
import { useProductStore } from '../stores/product-store';
import { useCanvasStore } from '../stores/canvas-store';
import { ProductLibrary } from '../components/ProductLibrary';
import { CanvasGrid } from '../components/CanvasGrid';
import { ExportModal } from '../components/ExportModal';
import { ProjectManager } from '../components/ProjectManager';
import { CardStylePanel } from '../components/CardStylePanel';
import ConfirmDialog from '@/components/ConfirmDialog';
import { updateProject, createProject } from '../services/project-service';
import type { LayoutType, StyleType, CanvasProject, CanvasData } from '../types';
import { StyleTypeLabels } from '../types';

const layoutOptions: { type: LayoutType; label: string; icon: React.ReactNode }[] = [
  { type: 'grid_2x2', label: '2×2', icon: <Grid2X2 className="w-4 h-4" /> },
  { type: 'grid_3x3', label: '3×3', icon: <Grid3X3 className="w-4 h-4" /> },
  { type: 'grid_4x4', label: '4×4', icon: <LayoutGrid className="w-4 h-4" /> },
  { type: 'grid_9x9', label: '9×9', icon: <LayoutGrid className="w-4 h-4" /> },
  { type: 'grid_custom', label: '自定义', icon: <LayoutGrid className="w-4 h-4" /> },
];

const styleOptions: StyleType[] = ['standard', 'split', 'diagonal', 'magazine', 'minimal'];

export function PosterCanvasPage() {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showStyleDropdown, setShowStyleDropdown] = useState(false);
  const [showCustomLayoutModal, setShowCustomLayoutModal] = useState(false);
  const [customRowsInput, setCustomRowsInput] = useState(3);
  const [customColsInput, setCustomColsInput] = useState(5);
  const [showProjectManager, setShowProjectManager] = useState(false);
  const [projectManagerMode, setProjectManagerMode] = useState<'save' | 'load' | 'new'>('save');
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);

  const { fetchProducts, fetchCategoryStats } = useProductStore();
  const { 
    layoutType, 
    styleType, 
    setLayoutType, 
    setStyleType, 
    clearCanvas,
    getFilledCellCount,
    getCellCount,
    showAxisLabels,
    toggleAxisLabels,
    showBorder,
    showCardBackground,
    showCanvasBackground,
    canvasBackgroundColor,
    toggleBorder,
    toggleCardBackground,
    toggleCanvasBackground,
    setCustomLayout,
    customRows,
    customCols,
    currentProjectId,
    currentProjectName,
    setProjectInfo,
    isDirty,
    newProject,
    canvasData,
    markAsSaved,
    // 撤销/重做（内置）
    undo,
    redo,
    canUndo,
    canRedo,
    getHistoryInfo,
  } = useCanvasStore();

  useEffect(() => {
    fetchProducts();
    fetchCategoryStats();
  }, [fetchProducts, fetchCategoryStats]);
  
  // 显示保存消息
  useEffect(() => {
    if (saveMessage) {
      const timer = setTimeout(() => setSaveMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [saveMessage]);
  
  // 快速保存 - 直接保存到数据库，不弹窗
  const quickSave = useCallback(async () => {
    if (isSaving) return;
    
    // 如果没有项目ID，说明是新项目，打开弹窗让用户输入名称
    if (!currentProjectId) {
      setProjectManagerMode('save');
      setShowProjectManager(true);
      return;
    }
    
    setIsSaving(true);
    try {
      await updateProject(currentProjectId, {
        name: currentProjectName,
        layoutType,
        styleType,
        canvasData: canvasData as CanvasData,
        canvasSettings: { customRows, customCols },
      });
      markAsSaved();
      setSaveMessage(`项目 "${currentProjectName}" 已保存`);
    } catch (error) {
      console.error('保存失败:', error);
      setSaveMessage('保存失败，请重试');
    } finally {
      setIsSaving(false);
    }
  }, [currentProjectId, currentProjectName, layoutType, styleType, canvasData, customRows, customCols, markAsSaved, isSaving]);
  
  // 键盘快捷键
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + Z = 撤销
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (canUndo()) undo();
      }
      // Ctrl/Cmd + Shift + Z 或 Ctrl/Cmd + Y = 重做
      if (
        ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) ||
        ((e.ctrlKey || e.metaKey) && e.key === 'y')
      ) {
        e.preventDefault();
        if (canRedo()) redo();
      }
      // Ctrl/Cmd + S = 快速保存
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        quickSave();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, canUndo, canRedo, quickSave]);

  const filledCount = getFilledCellCount();
  const totalCount = getCellCount();
  const historyInfo = getHistoryInfo();
  
  // 另存为 - 打开弹窗
  const handleSaveAs = useCallback(() => {
    setProjectManagerMode('save');
    setShowProjectManager(true);
  }, []);
  
  // 打开加载对话框
  const handleOpenLoad = () => {
    setProjectManagerMode('load');
    setShowProjectManager(true);
  };
  
  // 项目保存成功回调
  const handleProjectSaved = (project: CanvasProject) => {
    setProjectInfo(project.id, project.name);
    setSaveMessage(`项目 "${project.name}" 已保存`);
  };
  
  // 项目加载成功回调
  const handleProjectLoaded = (project: CanvasProject) => {
    setSaveMessage(`已加载项目 "${project.name}"`);
  };
  
  // 新建项目 - 打开弹窗
  const handleNewProject = () => {
    if (isDirty) {
      setShowUnsavedConfirm(true);
      return;
    }
    setProjectManagerMode('new');
    setShowProjectManager(true);
  };
  
  // 新建项目回调
  const handleProjectCreated = (name: string, description?: string) => {
    newProject();
    setProjectInfo(undefined, name);
    setSaveMessage(`已创建新项目「${name}」`);
  };
  
  // 开始编辑项目名称
  const startEditingName = () => {
    setEditingName(currentProjectName);
    setIsEditingName(true);
  };
  
  // 保存项目名称
  const saveProjectName = () => {
    if (editingName.trim()) {
      setProjectInfo(currentProjectId, editingName.trim());
    }
    setIsEditingName(false);
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* 苹果风格双层 Header */}
      <header className={`sticky top-0 z-50 ${
        isDarkMode ? 'bg-gray-800/95 backdrop-blur-xl' : 'bg-white/95 backdrop-blur-xl'
      }`}>
        {/* 第一层：主导航 - Logo、项目名、导出 */}
        <div className={`flex items-center justify-between px-5 py-2.5 border-b ${
          isDarkMode ? 'border-gray-700/50' : 'border-gray-200/50'
        }`}>
          {/* 左侧：Logo + 项目信息 */}
          <div className="flex items-center gap-5">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-sm">
                <Grid3X3 className="w-5 h-5 text-white" />
              </div>
              <span className={`font-semibold text-[15px] hidden sm:block ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                LED<span className="text-blue-600">Grid</span>
              </span>
            </div>
            
            {/* 分隔线 */}
            <div className={`w-px h-5 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
            
            {/* 项目名称（可编辑）*/}
            <div className="flex items-center gap-2">
              {isEditingName ? (
                <input
                  type="text"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onBlur={saveProjectName}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveProjectName();
                    if (e.key === 'Escape') setIsEditingName(false);
                  }}
                  className={`bg-transparent border-none outline-none text-[15px] font-medium w-40 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}
                  autoFocus
                />
              ) : (
                <button 
                  onClick={startEditingName}
                  className={`flex items-center gap-2 text-[15px] font-medium transition-colors ${
                    isDarkMode 
                      ? 'text-gray-200 hover:text-white' 
                      : 'text-gray-700 hover:text-gray-900'
                  }`}
                  title="点击编辑项目名称"
                >
                  {currentProjectName}
                  <Edit3 className="w-3 h-3 opacity-40" />
                </button>
              )}
              {isDirty && (
                <span className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" title="有未保存的更改" />
              )}
              {currentProjectId && !isDirty && (
                <Check className="w-3.5 h-3.5 text-green-500" title="已保存" />
              )}
            </div>
          </div>
          
          {/* 右侧：导出 + 暗黑模式 */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowExportModal(true)}
              disabled={filledCount === 0}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-b from-blue-500 to-blue-600 text-white text-[13px] font-semibold rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">导出海报</span>
            </button>

            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
              }`}
              title={isDarkMode ? '切换到浅色模式' : '切换到深色模式'}
            >
              {isDarkMode ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
            </button>
          </div>
        </div>
        
        {/* 第二层：工具栏 - 布局、操作工具 */}
        <div className={`flex items-center justify-between px-5 py-2 border-b ${
          isDarkMode ? 'border-gray-700/50 bg-gray-800/50' : 'border-gray-100 bg-gray-50/50'
        }`}>
          {/* 左侧：布局选择 */}
          <div className="flex items-center gap-3">
            <span className={`text-[11px] font-medium uppercase tracking-wider ${
              isDarkMode ? 'text-gray-500' : 'text-gray-400'
            }`}>
              布局
            </span>
            <div className={`flex items-center gap-0.5 p-0.5 rounded-lg ${
              isDarkMode ? 'bg-gray-700/50' : 'bg-white shadow-sm border border-gray-200/80'
            }`}>
              {layoutOptions.map((option) => (
                <button
                  key={option.type}
                  onClick={() => {
                    if (option.type === 'grid_custom') {
                      setShowCustomLayoutModal(true);
                    } else {
                      setLayoutType(option.type);
                    }
                  }}
                  className={`px-3 py-1.5 rounded-md text-[13px] font-medium transition-all ${
                    layoutType === option.type
                      ? isDarkMode
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-blue-500 text-white shadow-sm'
                      : isDarkMode
                        ? 'text-gray-400 hover:text-white hover:bg-gray-600/50'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {option.type === 'grid_custom' && layoutType === 'grid_custom' 
                    ? `${customRows}×${customCols}` 
                    : option.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* 中间：撤销重做 + 项目操作 */}
          <div className="flex items-center gap-4">
            {/* 撤销/重做 */}
            <div className="flex items-center gap-1">
              <button
                onClick={undo}
                disabled={!canUndo()}
                className={`p-1.5 rounded-md transition-colors relative ${
                  canUndo() 
                    ? isDarkMode 
                      ? 'text-gray-300 hover:bg-gray-700 hover:text-white' 
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    : isDarkMode
                      ? 'text-gray-600 cursor-not-allowed'
                      : 'text-gray-300 cursor-not-allowed'
                }`}
                title={`撤销 (⌘Z)${historyInfo.lastAction ? ` - ${historyInfo.lastAction}` : ''}`}
              >
                <Undo2 className="w-4 h-4" />
              </button>
              <button
                onClick={redo}
                disabled={!canRedo()}
                className={`p-1.5 rounded-md transition-colors relative ${
                  canRedo() 
                    ? isDarkMode 
                      ? 'text-gray-300 hover:bg-gray-700 hover:text-white' 
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    : isDarkMode
                      ? 'text-gray-600 cursor-not-allowed'
                      : 'text-gray-300 cursor-not-allowed'
                }`}
                title="重做 (⌘Y)"
              >
                <Redo2 className="w-4 h-4" />
              </button>
            </div>
            
            {/* 分隔线 */}
            <div className={`w-px h-4 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
            
            {/* 项目操作 */}
            <div className="flex items-center gap-1">
              <button
                onClick={handleNewProject}
                className={`p-1.5 rounded-md transition-colors ${
                  isDarkMode 
                    ? 'text-gray-300 hover:bg-gray-700 hover:text-green-400' 
                    : 'text-gray-600 hover:bg-gray-100 hover:text-green-600'
                }`}
                title="新建项目 (⌘N)"
              >
                <FilePlus className="w-4 h-4" />
              </button>
              <button
                onClick={handleOpenLoad}
                className={`p-1.5 rounded-md transition-colors ${
                  isDarkMode 
                    ? 'text-gray-300 hover:bg-gray-700 hover:text-blue-400' 
                    : 'text-gray-600 hover:bg-gray-100 hover:text-blue-600'
                }`}
                title="项目列表"
              >
                <FolderOpen className="w-4 h-4" />
              </button>
              {/* 保存按钮 - 直接保存（有ID时）或弹窗（新项目时） */}
              <button
                onClick={quickSave}
                disabled={isSaving}
                className={`p-1.5 rounded-md transition-colors relative ${
                  isSaving
                    ? 'text-gray-400 cursor-not-allowed'
                    : isDirty 
                      ? 'text-orange-500 bg-orange-500/10 hover:bg-orange-500/20' 
                      : isDarkMode 
                        ? 'text-gray-300 hover:bg-gray-700 hover:text-blue-400' 
                        : 'text-gray-600 hover:bg-gray-100 hover:text-blue-600'
                }`}
                title={currentProjectId 
                  ? (isDirty ? '保存更改 (⌘S)' : '已保存 (⌘S)') 
                  : '保存新项目 (⌘S)'
                }
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
              </button>
              {/* 另存为按钮 - 总是打开弹窗 */}
              <button
                onClick={handleSaveAs}
                className={`p-1.5 rounded-md transition-colors ${
                  isDarkMode 
                    ? 'text-gray-300 hover:bg-gray-700 hover:text-purple-400' 
                    : 'text-gray-600 hover:bg-gray-100 hover:text-purple-600'
                }`}
                title="另存为..."
              >
                <SaveAll className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* 右侧：辅助工具 */}
          <div className="flex items-center gap-2">
            <button
              onClick={toggleAxisLabels}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] font-medium transition-all ${
                showAxisLabels 
                  ? 'bg-blue-500/10 text-blue-600 border border-blue-500/20' 
                  : isDarkMode
                    ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              }`}
              title="显示/隐藏坐标轴标题"
            >
              <Tag className="w-3.5 h-3.5" />
              <span className="hidden lg:inline">轴标题</span>
            </button>
            
            {/* 边框开关 */}
            <button
              onClick={toggleBorder}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] font-medium transition-all ${
                showBorder 
                  ? isDarkMode
                    ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                  : 'bg-purple-500/10 text-purple-600 border border-purple-500/20'
              }`}
              title={showBorder ? '点击隐藏边框' : '点击显示边框'}
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" strokeDasharray={showBorder ? "0" : "4 2"} />
              </svg>
              <span className="hidden lg:inline">{showBorder ? '边框' : '无边框'}</span>
            </button>
            
            {/* 背景色开关 */}
            <button
              onClick={toggleCardBackground}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] font-medium transition-all ${
                showCardBackground 
                  ? isDarkMode
                    ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                  : 'bg-purple-500/10 text-purple-600 border border-purple-500/20'
              }`}
              title={showCardBackground ? '点击移除背景' : '点击显示背景'}
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" fill={showCardBackground ? "currentColor" : "none"} fillOpacity="0.2" />
              </svg>
              <span className="hidden lg:inline">{showCardBackground ? '卡片背景' : '透明卡片'}</span>
            </button>
            
            <button
              onClick={clearCanvas}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] font-medium transition-all ${
                isDarkMode
                  ? 'text-red-400 hover:bg-red-500/10'
                  : 'text-red-500 hover:bg-red-50'
              }`}
              title="清空画布"
            >
              <Trash2 className="w-3.5 h-3.5" />
              <span className="hidden lg:inline">清空</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex h-[calc(100vh-100px)]">
        {/* Left: Card Style Panel (Collapsible) */}
        <CardStylePanel isDarkMode={isDarkMode} />
        
        {/* Canvas Area */}
        <div className="flex-1 p-4 overflow-auto flex flex-col">
          <div className={`flex-1 flex flex-col max-w-5xl mx-auto w-full ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {/* Canvas Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-4">
                <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                  PREVIEW: {layoutType === 'grid_custom' 
                    ? `${customRows}X${customCols}` 
                    : layoutType.replace('grid_', '').toUpperCase()} LAYOUT
                </h2>
                
                {/* Style Selector */}
                <div className="relative">
                  <button
                    onClick={() => setShowStyleDropdown(!showStyleDropdown)}
                    className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
                      isDarkMode 
                        ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {StyleTypeLabels[styleType]}
                    <ChevronDown className="w-3 h-3" />
                  </button>
                  
                  {showStyleDropdown && (
                    <div className={`absolute top-full left-0 mt-1 rounded-lg shadow-lg border py-1 z-50 min-w-[140px] ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-700' 
                        : 'bg-white border-gray-200'
                    }`}>
                      {styleOptions.map((style) => (
                        <button
                          key={style}
                          onClick={() => {
                            setStyleType(style);
                            setShowStyleDropdown(false);
                          }}
                          className={`w-full px-3 py-1.5 text-left text-sm transition-colors ${
                            styleType === style 
                              ? 'text-blue-500 bg-blue-500/10' 
                              : isDarkMode 
                                ? 'text-gray-300 hover:bg-gray-700' 
                                : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {StyleTypeLabels[style]}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <span className="text-sm text-gray-400">
                1080 × 1080px per cell
              </span>
            </div>

            {/* Canvas - 自适应容器 */}
            <div 
              className={`flex-1 rounded-xl p-4 ${showCanvasBackground ? 'shadow-lg' : ''} min-h-0`}
              style={{ 
                backgroundColor: showCanvasBackground ? canvasBackgroundColor : 'transparent'
              }}
            >
              <CanvasGrid isDarkMode={isDarkMode} />
            </div>

            {/* Status */}
            <div className="mt-2 text-center text-sm text-gray-500">
              {filledCount} / {totalCount} cells filled
            </div>
          </div>
        </div>

        {/* Product Library Sidebar */}
        <div className={`w-80 border-l ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <ProductLibrary isDarkMode={isDarkMode} />
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
        />
      )}
      
      {/* Project Manager Modal */}
      <ProjectManager
        isOpen={showProjectManager}
        onClose={() => setShowProjectManager(false)}
        mode={projectManagerMode}
        onProjectSaved={handleProjectSaved}
        onProjectLoaded={handleProjectLoaded}
        onProjectCreated={handleProjectCreated}
      />
      
      {/* Save Message Toast */}
      {saveMessage && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg z-50 animate-fade-in">
          {saveMessage}
        </div>
      )}

      <ConfirmDialog
        open={showUnsavedConfirm}
        title="未保存的更改"
        message="当前项目有未保存的更改，确定要新建吗？"
        variant="warning"
        confirmText="新建"
        onConfirm={() => { setShowUnsavedConfirm(false); setProjectManagerMode('new'); setShowProjectManager(true); }}
        onCancel={() => setShowUnsavedConfirm(false)}
      />

      {/* Custom Layout Modal */}
      {showCustomLayoutModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-sm overflow-hidden shadow-xl">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">自定义布局</h2>
              <p className="text-sm text-gray-500 mt-1">设置网格的行数和列数</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  行数 (1-12)
                </label>
                <input
                  type="number"
                  min={1}
                  max={12}
                  value={customRowsInput}
                  onChange={(e) => setCustomRowsInput(Math.min(12, Math.max(1, Number(e.target.value))))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  列数 (1-12)
                </label>
                <input
                  type="number"
                  min={1}
                  max={12}
                  value={customColsInput}
                  onChange={(e) => setCustomColsInput(Math.min(12, Math.max(1, Number(e.target.value))))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                预览: <span className="font-semibold">{customRowsInput}×{customColsInput}</span> = {customRowsInput * customColsInput} 个格子
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowCustomLayoutModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={() => {
                  setCustomLayout(customRowsInput, customColsInput);
                  setShowCustomLayoutModal(false);
                }}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                应用
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PosterCanvasPage;
